"use client"

import type { FC, SVGProps } from "react"
import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import {
	IconCheckOutline18 as Check,
	IconChevronDownOutline18 as ChevronDown,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"

export const InternalSelect = React.memo(SelectPrimitive.Root)
export const SelectGroup = React.memo(SelectPrimitive.Group)
export const SelectContent = React.memo(SelectPrimitive.Content)
export const InternalSelectValue = React.memo(SelectPrimitive.Value)

export const InternalSelectTrigger = React.memo(
	React.forwardRef<
		React.ElementRef<typeof SelectPrimitive.Trigger>,
		React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
			isError: boolean
			Icon?: LucideIcon
			iconClassName?: string
			chevronClassName?: string
		}
	>(
		(
			{
				className,
				children,
				isError,
				Icon,
				iconClassName,
				chevronClassName,
				...props
			},
			ref,
		) => (
			<SelectPrimitive.Trigger
				ref={ref}
				className={cn(
					"flex h-8 w-full items-center gap-2 rounded-md bg-white pl-3 pr-2 text-sm shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:outline-none disabled:cursor-not-allowed dark:bg-gray-900 dark:text-gray-50",
					isError
						? "ring-red-300 dark:ring-red-700"
						: "ring-black/15 dark:ring-white/15",
					className,
				)}
				{...props}
			>
				{Icon && <Icon className={cn("h-4 w-4", iconClassName)} />}
				<span className="flex-1 truncate text-left">{children}</span>
				<ChevronDown
					className={cn("h-3 w-3 opacity-50", chevronClassName)}
					strokeWidth={1.75}
				/>
			</SelectPrimitive.Trigger>
		),
	),
)

export const InternalSelectContent = React.memo(
	React.forwardRef<
		React.ElementRef<typeof SelectPrimitive.Content>,
		React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
	>(({ className, children, position = "popper", ...props }, ref) => (
		<SelectPrimitive.Portal>
			<SelectPrimitive.Content
				ref={ref}
				className={cn(
					"relative z-50 min-w-[8rem] overflow-hidden rounded-md bg-white text-primary shadow-md ring-1 ring-black/10 dark:bg-gray-900 dark:ring-white/10",
					"!duration-150 ease-out-cubic data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
					position === "popper" &&
						"data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
					className,
				)}
				position={position}
				{...props}
			>
				<SelectPrimitive.Viewport
					className={cn(
						"p-1",
						position === "popper" &&
							"h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
					)}
				>
					{children}
				</SelectPrimitive.Viewport>
			</SelectPrimitive.Content>
		</SelectPrimitive.Portal>
	)),
)

export const SelectLabel = React.memo(
	React.forwardRef<
		React.ElementRef<typeof SelectPrimitive.Label>,
		React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
	>(({ className, ...props }, ref) => (
		<SelectPrimitive.Label
			ref={ref}
			className={cn(
				"py-1.5 pl-8 pr-2 text-sm font-semibold text-gray-900 dark:text-gray-300",
				className,
			)}
			{...props}
		/>
	)),
)

export const SelectItem = React.memo(
	React.forwardRef<
		React.ElementRef<typeof SelectPrimitive.Item>,
		React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
	>(({ className, children, ...props }, ref) => (
		<SelectPrimitive.Item
			ref={ref}
			className={cn(
				"relative flex cursor-default select-none items-center rounded-md py-1.5 pl-2 pr-8 text-sm font-medium outline-none focus:bg-gray-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 dark:focus:bg-gray-800",
				className,
			)}
			{...props}
		>
			<span className="absolute right-2 flex h-3 w-3 shrink-0 items-center justify-center">
				<SelectPrimitive.ItemIndicator>
					<Check className="h-4 w-4" />
				</SelectPrimitive.ItemIndicator>
			</span>

			<SelectPrimitive.ItemText className="min-w-0 flex-1 truncate">
				{children}
			</SelectPrimitive.ItemText>
		</SelectPrimitive.Item>
	)),
)

export const SelectSeparator = React.memo(
	React.forwardRef<
		React.ElementRef<typeof SelectPrimitive.Separator>,
		React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
	>(({ className, ...props }, ref) => (
		<SelectPrimitive.Separator
			ref={ref}
			className={cn("-mx-1 my-1 h-px bg-gray-100 dark:bg-gray-700", className)}
			{...props}
		/>
	)),
)

type LucideIcon = FC<SVGProps<SVGSVGElement>>
